import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../../lib/api';
import { useAuth } from '../../components/LoginGate';
import { canWrite, ROLE_LABELS, type UserRole } from '../../lib/constants';

const USER_ROLES_FOR_SELECT: UserRole[] = ['owner', 'owner_2', 'staff'];

interface UserRow {
  id: number;
  username: string;
  full_name: string | null;
  email: string | null;
  role: string;
  team_id: number | null;
  team_name: string | null;
  is_active: boolean;
  created_at: string;
}

interface Team { id: number; name: string; }

const EMPTY_FORM = { username: '', password: '', full_name: '', email: '', role: 'staff' as UserRole, team_id: '' };

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState<UserRow[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<UserRow | null>(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const [ur, tr] = await Promise.all([apiFetch('/api/users'), apiFetch('/api/teams')]);
    if (ur.ok) setUsers(await ur.json());
    if (tr.ok) setTeams(await tr.json());
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  function openAdd() {
    setEditing(null);
    setForm({ ...EMPTY_FORM });
    setError('');
    setShowModal(true);
  }

  function openEdit(u: UserRow) {
    setEditing(u);
    setForm({ username: u.username, password: '', full_name: u.full_name || '', email: u.email || '', role: (u.role as UserRole), team_id: u.team_id ? String(u.team_id) : '' });
    setError('');
    setShowModal(true);
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload: Record<string, unknown> = {
        full_name: form.full_name || null,
        email: form.email || null,
        role: form.role,
        team_id: form.team_id ? Number(form.team_id) : null,
      };
      if (!editing) {
        payload.username = form.username;
        payload.password = form.password;
      } else if (form.password) {
        payload.password = form.password;
      }
//
      const res = await apiFetch(editing ? `/api/users/${editing.id}` : '/api/users', {
        method: editing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Failed'); return; }
      setShowModal(false);
      fetchAll();
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(u: UserRow) {
    await apiFetch(`/api/users/${u.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_active: !u.is_active }),
    });
    fetchAll();
  }

  // Route guard already handles access; local write-permission flag controls Add/Edit buttons.
  const writable = canWrite(me?.role);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Users</h1>
          <p className="page-subtitle">Manage team members and their access</p>
        </div>
        {writable && <button className="btn btn-primary" onClick={openAdd}>+ Add User</button>}
      </div>

      <div className="card">
        {loading ? <div className="empty-state">Loading…</div> : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Team</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id}>
                    <td style={{ fontWeight: 500 }}>{u.full_name || '—'}</td>
                    <td style={{ color: 'var(--text-secondary)' }}>{u.username}</td>
                    <td style={{ color: 'var(--text-secondary)' }}>{u.email || '—'}</td>
                    <td>
                      <span className={`badge ${u.role === 'owner' ? 'badge-red' : u.role === 'owner_2' ? 'badge-blue' : 'badge-gray'}`}>{ROLE_LABELS[u.role as UserRole] ?? u.role}</span>
                    </td>
                    <td>{u.team_name || '—'}</td>
                    <td>
                      <span className={`badge ${u.is_active ? 'badge-green' : 'badge-gray'}`}>{u.is_active ? 'Active' : 'Inactive'}</span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6 }}>
                        {writable ? (
                          <>
                            <button className="btn-icon" onClick={() => openEdit(u)}>Edit</button>
                            {u.id !== me?.id && (
                              <button className="btn-icon" style={{ color: u.is_active ? 'var(--danger)' : 'var(--success)' }} onClick={() => toggleActive(u)}>
                                {u.is_active ? 'Disable' : 'Enable'}
                              </button>
                            )}
                          </>
                        ) : <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>View only</span>}
                      </div>
                    </td>
                  </tr>
                ))}
                {users.length === 0 && <tr><td colSpan={7} className="empty-state">No users found</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <h2>{editing ? 'Edit User' : 'Add User'}</h2>
              <button className="btn-icon" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSave}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Full Name</label>
                    <input className="form-input" value={form.full_name} onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))} placeholder="Full name" />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input className="form-input" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="Email address" />
                  </div>
                </div>
                {!editing && (
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Username *</label>
                      <input className="form-input" value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} required placeholder="Username" />
                    </div>
                  </div>
                )}
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">{editing ? 'New Password (leave blank to keep)' : 'Password *'}</label>
                    <input className="form-input" type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required={!editing} placeholder="At least 6 characters" />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Role</label>
                    <select className="form-input" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value as UserRole }))}>
                      {USER_ROLES_FOR_SELECT.map(r => (
                        <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Team</label>
                    <select className="form-input" value={form.team_id} onChange={e => setForm(f => ({ ...f, team_id: e.target.value }))}>
                      <option value="">No team</option>
                      {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                    </select>
                  </div>
                </div>
                {error && <div style={{ background: 'var(--danger-light)', color: 'var(--danger)', padding: '8px 12px', borderRadius: 6, fontSize: 13 }}>{error}</div>}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : editing ? 'Save Changes' : 'Create User'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
